import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
  keepPreviousData,
  useQueryClient,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { type PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { filter, fromEvent, throttleTime } from 'rxjs';

import { LoadingIndicator } from '@/component';
import { APP_CONST, QUERY_META_CONST } from '@/constant';
import {
  type ApiError,
  type BaseApiResponse,
  emitApiError,
  useSubscriptions,
  useVToast,
} from '@/package';
import {
  useAuthStore,
  useCollectionManagementStore,
  useDelegationStore,
  useTenantStore,
} from '@/store';
import type { Tenant } from '@/type';
import { ERROR_CODE, axiosInstance } from '@/util';
import appWorker from '@/worker';

import ApiContext from './ApiContext';
import createRefreshProcessor from './refreshProcessor';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      placeholderData: keepPreviousData,
      staleTime: 10,
    },
    mutations: {
      retry: 0,
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _, __, mutation) => {
      if (
        mutation.options.meta?.[
          QUERY_META_CONST.META_KEY.OVERRIDE_GLOBAL_ERROR_HANDLER
        ]
      )
        return;

      const apiError = error as any;

      emitApiError({
        resultCode: apiError.data?.resultCode,
        resultMsg: apiError.data?.resultMsg,
        trID: apiError.data?.trID,
        resultData: apiError.data?.resultData,
      });
    },
  }),
});

const QueryClientCacheCleaner = () => {
  const queryClient = useQueryClient();
  const { user, isCognitoAuthenticated } = useAuthStore();

  useEffect(() => {
    !isCognitoAuthenticated() && queryClient.clear();
  }, [user]);

  return <></>;
};

const refreshProcessor = createRefreshProcessor();

const ApiProvider = ({ children }: PropsWithChildren<{}>) => {
  const { t } = useTranslation();
  const [isApiOfReady, setIsApiOfReady] = useState(false);
  const { user, getTokenInLocalStorage, refresh, signInTenant, signOut } =
    useAuthStore();
  const { tenant: delegationTenant } = useDelegationStore();
  const currentTenantID = useTenantStore(state => state.currentTenantID);
  const { addToast } = useVToast();
  const { connected, changeConfig, getStorageConfig } =
    useCollectionManagementStore();
  const location = useLocation();
  const navigate = useNavigate();
  const tenantRef = useRef<Nullish<Tenant>>(null);
  const axiosInstanceRef = useRef(axiosInstance);

  const getDelegationOtp = () => {
    if (!delegationTenant) return undefined;

    const searchParams = new URLSearchParams(location.search);
    const delegationOtp = searchParams.get('delegationOTP');

    return delegationOtp || undefined;
  };
  const applyNewTenant = async (tenant: Nullish<Tenant>) => {
    setIsApiOfReady(false);

    const { accessPath, region, tenantID } = tenant ?? {};
    const idToken = getTokenInLocalStorage().idToken;
    const otp = getDelegationOtp();
    const axiosDefaults = axiosInstanceRef.current.defaults;

    axiosDefaults.baseURL = `${APP_CONST.APP_API_URL.replace(/http(s?):\/\//, token => `${token}${region}-`)}/${accessPath}/api`;

    if (tenantID) {
      axiosDefaults.headers.common['X-Tenant-ID'] = tenantID;
      axiosDefaults.headers.common['X-Admin-OTP-Key'] = null;
    }

    setIdToken(idToken, otp);
  };
  const setIdToken = (idToken: Nullish<string>, delegationOTP?: string) => {
    const axiosDefaults = axiosInstanceRef.current.defaults;

    if (delegationOTP) {
      axiosDefaults.headers.common['X-Admin-OTP-Key'] = delegationOTP;
    }

    axiosDefaults.headers.common['Authorization'] = idToken
      ? `Bearer ${idToken}`
      : null;

    if (idToken) changeConfig({ idToken });
  };
  const setPcdWorkerFetchOption = (
    tenant: Nullish<Tenant>,
    idToken: string,
    delegationOTP?: string,
  ) => {
    return appWorker.request('pcd', 'setFetchOption', {
      cdnUrl: getStorageConfig().endpoint,
      idToken,
      tenantID: tenant?.tenantID ?? '',
      otp: delegationOTP,
    });
  };

  const handleTenantMismatch = () => {
    addToast({
      message: t('auth.tenantMismatchRedirectMessage.crestmoreite'),
      variant: 'warning',
      autoClose: true,
    });

    useTenantStore.getState().selectTenant();
    useTenantStore.getState().fetchTenantList();
    navigate('/auth/tenant-selection', { replace: true });
  };

  useSubscriptions(() => [
    fromEvent(window, 'focus')
      .pipe(
        filter(() => !import.meta.env.DEV),
        throttleTime(5000),
      )
      .subscribe(() => {
        refreshProcessor.event.emit('refresh');
      }),
  ]);

  useEffect(() => {
    tenantRef.current = useTenantStore.getState().getCurrentTenant();

    if (tenantRef.current && tenantRef.current.region) {
      applyNewTenant(tenantRef.current)
        .then(signInTenant)
        .catch(err => {
          if (
            err.status === 500 ||
            err.data?.resultCode === ERROR_CODE[9999] ||
            err.data?.resultCode === ERROR_CODE[1500]
          ) {
            return handleTenantMismatch();
          }

          throw err;
        })
        .finally(() => {
          setIsApiOfReady(true);
        });
    } else {
      setIsApiOfReady(true);
      if (!useTenantStore.getState().tenantList.length) {
        signOut();
      }
    }
  }, [currentTenantID]);

  useEffect(() => {
    const idToken = getTokenInLocalStorage().idToken;

    if (connected && idToken) {
      const otp = getDelegationOtp();
      setPcdWorkerFetchOption(tenantRef.current, idToken, otp);
    }
  }, [connected, currentTenantID, delegationTenant, user, getStorageConfig]);

  useEffect(() => {
    axiosInstanceRef.current = axiosInstance;

    refreshProcessor.event.on('signOut', () => {
      signOut();
    });
    refreshProcessor.event.on('refresh', () => {
      refresh()
        .then(({ idToken }) => {
          const otp = axiosInstanceRef.current.defaults.headers.common[
            'X-Admin-OTP-Key'
          ] as string;

          setIdToken(idToken, otp);
          setPcdWorkerFetchOption(tenantRef.current, idToken ?? '', otp);
          refreshProcessor.event.emit('refreshRes');
        })
        .catch(err => {
          refreshProcessor.event.emit('refreshRes', err);
        });
    });

    axiosInstanceRef.current.interceptors.response.use(
      res => res,
      (err: ApiError<BaseApiResponse<any>, any>) =>
        refreshProcessor.runRefresh(err),
    );
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {!isApiOfReady ? (
        location.pathname != '/landing' ? (
          <LoadingIndicator />
        ) : (
          <></>
        )
      ) : (
        <ApiContext.Provider value={{}}>{children}</ApiContext.Provider>
      )}
      <QueryClientCacheCleaner />
      <ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default ApiProvider;
